import React from "react";
import { InlineMath } from "react-katex";
import { dynamicText } from "../config/config.js";
import { variabilize, chooseVariables } from "./variabilize.js";
import Spacer from "@components/Spacer";
import ErrorBoundary from "@components/ErrorBoundary";
import RenderMedia from "@components/RenderMedia";
import { CONTENT_SOURCE } from "@common/global-config";

// KaTeX is strict about Unicode characters inside math. Some authored content
// (and some LLM output) uses unicode symbols like `°` / `º` directly, which
// causes KaTeX to throw and can crash the page if not handled.
function sanitizeLatexMath(math) {
    if (typeof math !== "string") return math;
    return math
        // Degrees
        .replace(/[°º]/g, "^{\\circ}")
        // Common operators/relations
        .replace(/×/g, "\\times ")
        .replace(/÷/g, "\\div ")
        .replace(/±/g, "\\pm ")
        .replace(/≈/g, "\\approx ")
        .replace(/≤/g, "\\leq ")
        .replace(/≥/g, "\\geq ")
        .replace(/≠/g, "\\neq ")
        // Unicode minus to ASCII hyphen-minus
        .replace(/−/g, "-");
}

/**
 * @param {string|*} text
 * @param problemID
 * @param {*} variabilization
 * @param context
 */
function renderText(text, problemID, variabilization, context) {
    if (typeof text !== "string") {
        return text;
    }
    text = text.replaceAll("\\neq", "≠");
    text = text.replaceAll("**", "^");
    let result = text;
    result = parseForMetaVariables(result, context);

    for (const d in dynamicText) {
        const replace = dynamicText[d];
        result = result.split(d).join(replace); // expands all "%dynamic%" text to their specific counterparts
    }
    if (variabilization) {
        result = variabilize(result, variabilization);
    }

    const lines = result.split("\\n");
    return lines.map((line, idx) => {
        /**
         * If line has LaTeX, split by the "&&" delimiter to separate plain text from LaTeX
         * @type {(string | JSX.Element)[]}
         */
        let lineParts = line.split("$$");
        lineParts = lineParts.map((part, jdx) => {
            const isLaTeX = jdx % 2 !== 0; // implies it is in between two "$$" delimiters
            if (isLaTeX) {
                const regex = /^_{3,}$/;
                if (regex.test(part)) {
                    return parseForFillInQuestions(part);
                }
                return (
                    <ErrorBoundary
                        componentName={"InlineMath"}
                        replacement={part}
                        inline
                        key={Math.random() * 2 ** 16}
                    >
                        <InlineMath
                            math={sanitizeLatexMath(part)}
                            renderError={(error) => {
                                // Never crash the whole UI for a single bad token.
                                // Show raw math text instead.
                                // eslint-disable-next-line no-console
                                console.warn("[katex] render error in authored content:", error);
                                return part;
                            }}
                        />
                    </ErrorBoundary>
                );
            }

            const lineSubParts = part.split("##");
            return lineSubParts.map((subPart, kdx) => {
                const isMedia = kdx % 2 !== 0;
                if (isMedia) {
                    return (
                        <center key={Math.random() * 2 ** 16}>
                            <RenderMedia
                                url={subPart}
                                problemID={problemID}
                                contentSource={CONTENT_SOURCE}
                            />
                        </center>
                    );
                }
                return parseForFillInQuestions(subPart);
            });
        });
        // add a spacer if it isn't the last line
        if (idx !== lines.length - 1) {
            lineParts.push(
                <Spacer height={2} width={2} key={Math.random() * 2 ** 16} />
            );
        }
        return lineParts;
    });
}

/**
 * Renders the text generated from ChatGPT.
 * @param {string|*} text
 * @param problemID
 * @param {*} variabilization
 * @param context
 */
function renderGPTText(text, problemID, variabilization, context) {
    if (typeof text !== "string") {
        return text;
    }
    text = preprocessChatGPTResponse(text);

    text = text.replaceAll("\\neq", "≠");
    text = text.replaceAll("**", "^");
    let result = text;
    result = parseForMetaVariables(result, context);

    for (const d in dynamicText) {
        const replace = dynamicText[d];
        result = result.split(d).join(replace); // expands all "%dynamic%" text to their specific counterparts
    }
    if (variabilization) {
        result = variabilize(result, variabilization);
    }

    let lines = result.split("\\n");
    lines = lines.map((line, idx) => {
        /**
         * If line has LaTeX, split by the "&&" delimiter to separate plain text from LaTeX
         * @type {(string | JSX.Element)[]}
         */
        let lineParts = line.split("$$");
        lineParts = lineParts.map((part, jdx) => {
            const isLaTeX = jdx % 2 !== 0; // implies it is in between two "$$" delimiters
            if (isLaTeX) {
                const regex = /^_{3,}$/;
                if (regex.test(part)) {
                    return parseForFillInQuestions(part);
                }
                return (
                    <ErrorBoundary
                        componentName={"InlineMath"}
                        replacement={part}
                        inline
                        key={Math.random() * 2 ** 16}
                    >
                        <InlineMath
                            math={sanitizeLatexMath(part)}
                            renderError={(error) => {
                                // Never crash the whole UI for a single bad token.
                                // Show raw math text instead.
                                // eslint-disable-next-line no-console
                                console.warn("[katex] render error in GPT content:", error);
                                return part;
                            }}
                        />
                    </ErrorBoundary>
                );
            }

            const lineSubParts = part.split("##");
            return lineSubParts.map((subPart, kdx) => {
                const isMedia = kdx % 2 !== 0;
                if (isMedia) {
                    return (
                        <center key={Math.random() * 2 ** 16}>
                            <RenderMedia
                                url={subPart}
                                problemID={problemID}
                                contentSource={CONTENT_SOURCE}
                            />
                        </center>
                    );
                }
                return parseForFillInQuestions(subPart);
            });
        });

        // add a spacer if it isn't the last line
        if (idx !== lines.length - 1) {
            lineParts.push(
                <Spacer height={2} width={2} key={Math.random() * 2 ** 16} />
            );
        }
        return lineParts;
    });
    return lines;
}

const META_REGEX = /%\{([^{}%"]+)}/g;
const mapper = {
    oats_user_id: (context) => context.userID,
};
/**
 * Takes in a string and iff there is a part that matches %{variable}, replace it with some context metadata
 * @param {string} str
 * @param context
 * @return {string}
 */
function parseForMetaVariables(str, context) {
    return str.replaceAll(META_REGEX, (ogMatch, group1) => {
        if (group1 in mapper) {
            return mapper[group1].call(this, context);
        }
        return ogMatch;
    });
}

function preprocessChatGPTResponse(input) {
    // Step 1: Replace ChatGPT '\n' new lines with '\\n'
    input = input.replace(/\n/g, "\\n");
    // Step 2: Replace monetary values ($12,000) with (\uFF04)12,000
    const moneyRegex = /\$(\d{1,3}(,\d{3})*(\.\d{2})?|(\d+))/g;
    input = input.replace(moneyRegex, (_, moneyValue) => `\uFF04${moneyValue}`);
    // Step 3: Convert any remaining single dollar signs (not part of monetary values) to double dollars
    input = input.replace(/(?<!\$)\$(?!\$)/g, "$$$$");
    // Step 4: Replace all instances of \uFF04 back to $
    input = input.replace(/\uFF04/g, "$");
    return input;
}

/**
 * Convert markdown links ([label](url)) into clickable anchors.
 * Only allows explicit external links and mailto links.
 * @param {string} str
 * @return {(string | JSX.Element)[]}
 */
function parseForHyperlinks(str) {
    if (typeof str !== "string" || str.length === 0) {
        return [str];
    }

    const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g;
    const result = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(str)) !== null) {
        const [fullMatch, label, url] = match;
        const start = match.index;

        if (start > lastIndex) {
            result.push(str.slice(lastIndex, start));
        }

        result.push(
            <a
                key={Math.random() * 2 ** 16}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
            >
                {label}
            </a>
        );

        lastIndex = start + fullMatch.length;
    }

    if (lastIndex < str.length) {
        result.push(str.slice(lastIndex));
    }

    return result.length > 0 ? result : [str];
}

/**
 * Takes in a string and iff there is 3+ underscores in a row, convert it into a fill-in-the-blank box.
 * @param {(string)} str
 * @return {(string | JSX.Element)[]}
 */
function parseForFillInQuestions(str) {
    const strParts = str.split(/_{3,}/);
    let result = [];
    strParts.forEach((part, idx) => {
        if (idx > 0) {
            result.push(
                <span
                    key={Math.random() * 2 ** 16}
                    aria-label={"fill in the blank"}
                    style={{
                        // TODO: choose between the following two styles
                        marginLeft: "0.5ch",
                        marginRight: "0.5ch",
                        paddingLeft: "2.5ch",
                        paddingRight: "2.5ch",
                        position: "relative",
                        background: "rgb(242,243,244)",
                        borderRadius: "0.6ch",
                    }}
                >
                    <div
                        style={{
                            position: "absolute",
                            bottom: 3.5,
                            left: 4,
                            right: 4,
                            height: 1.5,
                            borderRadius: "0.6ch",
                            background: "rgb(75,76,77)",
                        }}
                    />
                </span>
            );
        }
        result.push(...parseForHyperlinks(part));
    });
    return result;
}

export { renderText, renderGPTText, chooseVariables };
